// NodeTypesContext.js
// context/NodeTypesContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
} from 'react';
import {
  warmNodeTypesCache,
  getNodeTypesCacheForScope,
  makeBaseScopeKeyStrict,
  makeCompositeScopeKey,
  setActiveNodeTypesScope,
} from 'services/nodetypes/NodeTypesCacheService';
import {
  getNodeTypeLabel,
  getNodeTypePluralLabel,
} from 'utilities/NodeTypeLabels';

import { useBase } from 'context/BaseContext';

/**
 * NodeTypesProvider
 *
 * Props:
 * - db: Firestore client instance
 * - subscribe?: boolean (default true) — keep cache live
 * - ttlMs?: number (optional) — localStorage TTL for warm starts
 */
const Ctx = createContext(null);

export function NodeTypesProvider({ db, subscribe = true, ttlMs, children }) {
  const { activeBase } = useBase();

  // Always resolve against concrete scopes so shared UI (sidebar and node toolbar)
  // reads from the same node type cache path.
  const scopeKey = makeCompositeScopeKey([
    makeBaseScopeKeyStrict(activeBase?.key),
    'ROOT',
  ]);

  // Warm the cache once (and optionally keep it live).
  // Also keep the global _activeScopeKey in sync so module-level callers
  // (services, utilities called during user interactions) resolve correctly.
  useEffect(() => {
    (async () => {
      // Ensure global callers of getNodeTypesCache() see the right scope.
      setActiveNodeTypesScope(scopeKey);
      await warmNodeTypesCache(db, { scopeKey, subscribe, ttlMs });
    })();
  }, [db, scopeKey, subscribe, ttlMs]);

  // Subscribe to the currently selected scope cache.
  //
  // IMPORTANT: we return a primitive snapshot string (ready:rev) rather than
  // the cache object itself. The cache object is mutable and its reference
  // never changes, so Object.is() would always return true and React would
  // never schedule a re-render when the cache becomes ready. A primitive
  // string changes value when readiness or revision changes, allowing React
  // to correctly detect cache updates.
  // Use _emitCount (not rev) so that every cache._emit() produces a unique
  // snapshot string. A hash-based rev could theoretically collide or remain
  // unchanged across emits that don't alter the content (e.g. a subscription
  // re-delivering the same data after a fetchAll). The counter guarantees a
  // new string on every update, ensuring React always re-renders the provider.
  useSyncExternalStore(
    (cb) => getNodeTypesCacheForScope(scopeKey).onChange(cb),
    () => {
      const c = getNodeTypesCacheForScope(scopeKey);
      return `${c.ready ? '1' : '0'}:${c._emitCount}`;
    },
    () => {
      const c = getNodeTypesCacheForScope(scopeKey);
      return `${c.ready ? '1' : '0'}:${c._emitCount}`;
    }
  );

  // Read the cache directly after useSyncExternalStore has ensured we re-render
  // whenever the snapshot changes.
  const cache = getNodeTypesCacheForScope(scopeKey);

  // Scope-bound lookup — avoids the stale _activeScopeKey global that is only
  // updated inside useEffect (i.e. after the first render on base switch).
  const getTypeForScope = (key) => {
    if (!key) return undefined;
    return cache.get(key);
  };

  // Provide call-through functions that always hit the latest cache
  const value = {
    getType: getTypeForScope,
    getAll: () => {
      const list = cache.getAll?.();
      if (Array.isArray(list)) return list;
      if (cache.index?.byKey instanceof Map) return Array.from(cache.index.byKey.values());
      if (Array.isArray(cache.docs)) return cache.docs;
      return [];
    },
    getCount: () => {
      // Preferred: cache API
      const list = cache.getAll?.();
      if (Array.isArray(list)) return list.length;
      // Fallbacks: internal index shapes
      if (cache.index?.byKey instanceof Map) return cache.index.byKey.size;
      if (Array.isArray(cache.docs)) return cache.docs.length;
      return 0;
    },
    getRevision: () => cache.rev || '',
    isReady: () => cache.isReady?.() === true,
    getLabel: (key, language = 'en', options = {}) => getNodeTypeLabel(getTypeForScope, key, options),
    getLabelPlural: (key, language = 'en', options = {}) => getNodeTypePluralLabel(getTypeForScope, key, options),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * useNodeTypes
 * Returns { getType, getCount, isReady }.
 */
export function useNodeTypes() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useNodeTypes must be used within <NodeTypesProvider>');
  return ctx;
}
